import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Save, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const SECTIONS = ["critical", "personality", "eq"];
const DIFFICULTIES = ["easy", "medium", "hard"];
const TRAITS = ["O", "C", "E", "A", "N"];

const EMPTY = {
  section: "critical",
  category: "",
  difficulty: "medium",
  text: "",
  text_hi: "",
  options_en_lines: "",
  options_hi_lines: "",
  correct_index: 0,
  trait: "",
  reverse: false,
  weights_lines: "",
  order: 1,
};

export default function QuestionEditor({ open, onClose, question, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!question?.id;

  useEffect(() => {
    if (question) {
      setForm({
        section: question.section || "critical",
        category: question.category || "",
        difficulty: question.difficulty || "medium",
        text: question.text || "",
        text_hi: question.text_hi || "",
        options_en_lines: (question.options || []).join("\n"),
        options_hi_lines: (question.options_hi || []).join("\n"),
        correct_index: question.correct_index ?? 0,
        trait: question.trait || "",
        reverse: !!question.reverse,
        weights_lines: (question.weights || []).join("\n"),
        order: question.order || 1,
      });
    } else {
      setForm(EMPTY);
    }
  }, [question, open]);

  if (!open) return null;

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const options = form.options_en_lines.split("\n").map((s) => s.trim()).filter(Boolean);
      const options_hi_arr = form.options_hi_lines.split("\n").map((s) => s.trim()).filter(Boolean);
      const weights_arr = form.weights_lines
        .split("\n").map((s) => s.trim()).filter(Boolean)
        .map((s) => parseFloat(s)).filter((n) => !Number.isNaN(n));

      const payload = {
        section: form.section,
        category: form.category || null,
        difficulty: form.difficulty,
        text: form.text.trim(),
        text_hi: form.text_hi.trim() || null,
        options,
        options_hi: options_hi_arr.length ? options_hi_arr : null,
        correct_index: form.section === "critical" ? Number(form.correct_index) : null,
        trait: form.section === "personality" ? (form.trait || null) : null,
        reverse: form.section === "personality" ? !!form.reverse : false,
        weights: form.section === "eq" && weights_arr.length ? weights_arr : null,
        order: Number(form.order) || 0,
      };

      if (isEdit) {
        await api.patch(`/admin/questions/${question.id}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/admin/questions", payload);
        toast.success("Question added");
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const optionsList = form.options_en_lines.split("\n").filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 bg-primary/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="question-editor-modal">
      <div className="w-full max-w-2xl bg-white rounded-xl border border-border shadow-2xl overflow-hidden my-8">
        <div className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
          <span className="font-display font-bold">
            {isEdit ? t("admin.edit_title") : t("admin.create_title")}
          </span>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/15" data-testid="qe-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("admin.field_section")}>
              <select value={form.section} onChange={(e) => setF("section", e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-section">
                {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label={t("admin.field_difficulty")}>
              <select value={form.difficulty} onChange={(e) => setF("difficulty", e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-difficulty">
                {DIFFICULTIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label={t("admin.field_order")}>
              <input type="number" value={form.order} onChange={(e) => setF("order", e.target.value)}
                     className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-order" />
            </Field>
          </div>
          <Field label={t("admin.field_category")}>
            <input value={form.category} onChange={(e) => setF("category", e.target.value)}
                   placeholder="e.g. logic-reasoning"
                   className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-category" />
          </Field>
          <Field label={t("admin.field_text_en")}>
            <textarea required value={form.text} onChange={(e) => setF("text", e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-text-en" />
          </Field>
          <Field label={t("admin.field_text_hi")}>
            <textarea value={form.text_hi} onChange={(e) => setF("text_hi", e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-text-hi" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("admin.field_options_en")}>
              <textarea value={form.options_en_lines} onChange={(e) => setF("options_en_lines", e.target.value)}
                        rows={5}
                        className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm font-mono" data-testid="qe-options-en" />
            </Field>
            <Field label={t("admin.field_options_hi")}>
              <textarea value={form.options_hi_lines} onChange={(e) => setF("options_hi_lines", e.target.value)}
                        rows={5}
                        className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm font-mono" data-testid="qe-options-hi" />
            </Field>
          </div>

          {form.section === "critical" && (
            <Field label={t("admin.field_correct")}>
              <select value={form.correct_index} onChange={(e) => setF("correct_index", e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-correct">
                {optionsList.length === 0 && <option value={0}>Add options first</option>}
                {optionsList.map((o, i) => <option key={i} value={i}>{String.fromCharCode(65+i)}. {o.slice(0,40)}</option>)}
              </select>
            </Field>
          )}

          {form.section === "personality" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("admin.field_trait")}>
                <select value={form.trait} onChange={(e) => setF("trait", e.target.value)}
                        className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm" data-testid="qe-trait">
                  <option value="">—</option>
                  {TRAITS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={t("admin.field_reverse")}>
                <label className="inline-flex items-center gap-2 mt-2">
                  <input type="checkbox" checked={form.reverse} onChange={(e) => setF("reverse", e.target.checked)} data-testid="qe-reverse" />
                  <span className="text-sm text-muted-foreground">{t("admin.field_reverse")}</span>
                </label>
              </Field>
            </div>
          )}

          {form.section === "eq" && (
            <Field label={t("admin.field_weights")}>
              <textarea value={form.weights_lines} onChange={(e) => setF("weights_lines", e.target.value)}
                        placeholder="0.2&#10;1.0&#10;0.4&#10;0.3"
                        rows={4}
                        className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm font-mono" data-testid="qe-weights" />
            </Field>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm font-medium border border-border bg-white hover:bg-muted">
              {t("admin.cancel")}
            </button>
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50" data-testid="qe-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t("admin.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
